import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ---------- Types ----------

export interface OrgSettings {
  id: string;
  name: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  distanceUnit: 'miles' | 'kilometers';
  weightUnit: 'lbs' | 'kg';
  logo?: string;
  updatedAt: string;
}

// ---------- Settings Hooks ----------

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<{ data: OrgSettings }>('/settings');
      return res.data.data;
    },
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<OrgSettings>) => {
      // Server contract (UpdateSettingsSchema in settings.dto.ts) expects the
      // patch body wrapped under `settings`, not flat fields.
      const res = await api.patch<{ data: OrgSettings }>('/settings', { settings: data });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

// ---------- Company Info Hooks (WAYP-COMPANY-INFO) ----------

export type CurrencyCode = 'USD' | 'CAD' | 'MXN' | 'EUR' | 'GBP' | 'AUD' | 'INR';

export interface CompanyInfo {
  id: string;
  name: string;
  slug: string;
  country: string;
  defaultCurrency: CurrencyCode;
  // Org-scoped enabled currency list — drives currency dropdowns on
  // driver / carrier / supplier forms. Server backfills with
  // [defaultCurrency] when unset, so this is always at least length 1.
  enabledCurrencies: CurrencyCode[];
  // Billing Address
  address1: string | null;
  address2: string | null;
  city: string | null;
  stateProvince: string | null;
  zipPostal: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  dotNumber: string | null;
  mcNumber: string | null;
  businessType: string | null;
  // Accounting
  qbCustomerTerms: string | null;
  qbVendorTerms: string | null;
  // #1732: QuickBooks Desktop Terms ListID sibling.
  qbdCustomerTermsListId?: string | null;
  taxId: string | null;
  pstNumber: string | null;
  gstHstNumber: string | null;
  enableTaxes: boolean;
  defaultTaxCode: string | null;
  enableMultiCurrency: boolean;
  decimalPlaces: number;
  // Start Numbers
  invoicePrefix: string | null;
  startInvoiceNumber: number | null;
  loadPrefix: string | null;
  startLoadNumber: number | null;
  tripPrefix: string | null;
  startTripNumber: number;
  settlementPrefix: string | null;
  startSettlementNumber: number | null;
  // Regional
  distanceUnit: 'miles' | 'kilometers';
  timezone: string | null;
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  use24HourClock: boolean;
  // Org default language: staff-UI fallback for users with no personal choice,
  // and the language of documents sent to external contacts (customers/carriers).
  defaultLanguage: string;
}

export function useCompanyInfo() {
  return useQuery({
    queryKey: ['settings', 'company-info'],
    queryFn: async () => {
      const res = await api.get<{ data: CompanyInfo }>('/settings/company-info');
      return res.data.data;
    },
  });
}

export function useUpdateCompanyInfo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<CompanyInfo>) => {
      const res = await api.patch<{ data: CompanyInfo }>('/settings/company-info', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'company-info'] });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

// ---------- Company Preferences Hooks (WAYP-803) ----------
// V1 reference: /settings/preferences. Round 1 covers General-tab fields only;
// per-module tabs land in #804–#808.

export type InvoiceDateFive =
  | 'load_creation'
  | 'load_start'
  | 'load_completion'
  | 'load_end'
  | 'invoice_creation'
  | 'settlement_creation';

export type InvoiceDateFour =
  | 'load_creation'
  | 'load_start'
  | 'load_completion'
  | 'load_end';

// WAYP-825: promoted to a strict enum literal union matching the Prisma
// `DispatchGridDateType` enum on `Organization.dispatchGridDateType`. Values
// are persisted in upper-case at the DB level (`DATETIME` / `DATE`); the
// Company Preferences UI sends and receives them in the same shape.
export type DispatchGridDateType = 'DATETIME' | 'DATE';

// WAYP-804: V1 stores delay-charges as a JSON object keyed by module. Round 2
// persisted the `dispatch` key; round 3 (#805) adds `usps`. Per-module keys for
// Intermodal/FTL/Livestock land in #806–#808 — they're round-tripped on the
// wire today even before they have UI.
export interface DispatchDelayChargeEntry {
  other_charge_type?: string | null;
  waiting_time_allowance?: number | null;
  hourly_price?: number | null;
}
export interface CompanyDelayCharges {
  dispatch?: DispatchDelayChargeEntry | null;
  usps?: DispatchDelayChargeEntry | null;
  // WAYP-806: Intermodal sub-form added (round 4).
  intermodal?: DispatchDelayChargeEntry | null;
  // WAYP-807: FTL Brokerage sub-form added (round 5).
  ftlbrokerage?: DispatchDelayChargeEntry | null;
  // WAYP-808: Livestock sub-form added (round 6, FINAL).
  livestock?: DispatchDelayChargeEntry | null;
  [moduleKey: string]: DispatchDelayChargeEntry | null | undefined;
}

// WAYP-805: USPS Reconciliation enums.
export type ReconcileAgainst = 'All loads' | 'Completed loads';
export type LateMarkedAs = 'bold' | 'italic';
export type CheckLateSlipConsecutiveStops = 'first_stop' | 'last_stop' | 'both_stops';

// WAYP-806: Intermodal tab — V1 column-level enum divergences.
// `appointment_day_only_intermodal` stores 'datetime' | 'date' (string), not boolean.
// `enable_fsc_pay_intermodal` stores 'No' | 'For All Drivers' | 'For Owner Operators' (string), not boolean.
export type AppointmentDayOnlyMode = 'datetime' | 'date';
export type IntermodalEnableFscPayMode = 'No' | 'For All Drivers' | 'For Owner Operators';

// Dispatch tab — `enable_fsc_pay` is the same 3-value string as the other
// three modules (migration `20260528120000_dispatch_enable_fsc_pay_to_string`
// upgraded the legacy Boolean column). Aliasing the type rather than
// re-declaring it keeps the four modules visibly identical.
export type DispatchEnableFscPayMode = IntermodalEnableFscPayMode;

// WAYP-807: FTL Brokerage tab — same column-level enum divergences as Intermodal.
// V1 stores `appointment_day_only_ftlbrokerage` and `enable_fsc_pay_ftlbrokerage`
// using the same string enums as the Intermodal columns. Aliasing for clarity.
export type FtlBrokerageEnableFscPayMode = IntermodalEnableFscPayMode;

// WAYP-808: Livestock tab — same column-level enum divergences as Intermodal /
// FTL Brokerage. V1 stores `appointment_day_only_livestock` and
// `enable_fsc_pay_livestock` using the same string enums. Aliasing for clarity.
export type LivestockEnableFscPayMode = IntermodalEnableFscPayMode;

export interface CompanyPreferences {
  id: string;
  // General Preferences
  allowStatusChange: boolean;
  autoTextDriverEnRoute: boolean;
  autoEmailDriverEnRoute: boolean;
  showEmptyDestinationsOnSms: boolean;
  syncLocationLinkEld: boolean;
  driverTripTimeHoursThreshold: 11 | 14 | 16;
  driverTripTimeRules: 1 | 2 | 3;
  // Dispatch (Load Form/Grid) Preferences
  showRatesOnDriverConfirmation: boolean;
  enableQuickAddLocation: boolean;
  allowLatLongAddresses: boolean;
  showPinInLocationOnRouting: boolean;
  callEndShiftAsEdiLoad: boolean;
  showFutureLoadsInGeotabApp: boolean;
  enableAutoDistanceCalc: boolean;
  // Invoice Preferences
  invoiceDisplayDate: InvoiceDateFive | null;
  manualInvoice: boolean;
  invoiceScreenDate: InvoiceDateFour | null;
  editInvoiceNumber: boolean;
  emailOnDeletedOrCancelled: string | null;
  // Settlement Preferences
  settlementDisplayDate: InvoiceDateFive | null;
  enableBillNo: boolean;
  settlementFilterBasedOn: InvoiceDateFour | null;
  allowTripLoadsToCreateSettlement: boolean;

  // ── Dispatch tab (WAYP-804) ─────────────────────────────────────────
  // Loads / Status
  defaultLoadStatusId: string | null;
  loadStatusToHide: string[] | null;
  redTagLoad: boolean;
  // Appointment Times
  isAppointmentTimeMandatory: boolean;
  showAppointmentTimeInFormAndGrid: boolean;
  acceptDefaultAppointmentTimes: boolean;
  // V1 stores integer minutes for `auto_departure_time` (NOT a boolean) —
  // matches the Intermodal / FTL Brokerage / Livestock variants.
  autoDepartureTime: number | null;
  setDefaultAppointmentTime: boolean;
  appointmentDayOnly: boolean;
  allowOverlapAppointmentTime: boolean;
  // V1 Deadhead Miles — make appointment time mandatory when a driver is
  // assigned to a routing row (org-level single flag).
  makeAppointmentTimeMandatoryWhenDriverAssigned: boolean;
  // Categories / Quotes
  isLoadCategoryMandatory: boolean;
  allowCategories: boolean;
  enableQuotes: boolean;
  // Driver assignment / Trip
  autoAssignDriver: boolean;
  enableToBeDetermined: boolean;
  ltl: boolean;
  ignoreDriverAssignmentWarning: boolean;
  // Settlement options (dispatch tab)
  // V1 stores enum string (matches Intermodal / FTL Brokerage / Livestock).
  enableFscPay: DispatchEnableFscPayMode | null;
  enableSettlementOptionForEquipment: boolean;
  enablePaymentOnlyForDriver: boolean;
  // Documents / Exports
  enableCojoinedExports: boolean;
  enableDownloadDocument: boolean;
  // Rates / FSC / Routing
  // Waypoint: require Rate Type + Rate on dispatch loads (default true). When false, both become optional.
  isRateMandatory: boolean;
  /** ATL-001198 — Truck Identification from Native App → Support Multi-Truck
   *  Assignment on Load, one column per module. */
  supportMultiTruckAssignment: boolean;
  supportMultiTruckAssignmentUsps: boolean;
  supportMultiTruckAssignmentIntermodal: boolean;
  supportMultiTruckAssignmentFtlbrokerage: boolean;
  supportMultiTruckAssignmentLivestock: boolean;
  defaultRateTypeId: string | null;
  dispatchDefaultFuelSurchargeRateId: string | null;
  deadHeadRoutingRowTypeDispatch: number | null;
  // Display
  dispatchGridDateType: DispatchGridDateType | null;
  dispatchWarningColor: string | null;
  // Delay Charges (per-module JSON; `dispatch` editable in #804, `usps` editable in #805)
  delayCharges: CompanyDelayCharges | null;

  // ── USPS tab (WAYP-805) ────────────────────────────────────────────
  // #1216 USPS Legacy Cleanup (Andrew 2026-05-21): 6 USPS-prefs fields
  // dropped from Organization (Loads & Status / Driver Assignment /
  // Documents & Exports sections retired).
  // Categories / Quotes
  allowCategoriesUsps: boolean;
  isLoadCategoryMandatoryUsps: boolean;
  enableQuotesUspsCarrier: boolean;
  // Rates / FSC
  uspsDefaultFuelSurchargeRateId: string | null;
  // USPS EDI
  autoAcceptUspsTenderTimeChanges: boolean;
  // USPS Reconciliation
  reconcileAgainst: ReconcileAgainst | null;
  postTripAmountDiffToOtherCharge: string | null;
  postTripFuelDiffToOtherCharge: string | null;
  enableUspsLateColorLoadstatus: boolean;
  lateMarkedAs: LateMarkedAs | null;
  checkLateSlipConsecutiveStops: CheckLateSlipConsecutiveStops | null;

  // ── Intermodal tab (WAYP-806) ──────────────────────────────────────
  // Loads / Status (Intermodal subset of dispatch family)
  defaultLoadStatusIdIntermodal: string | null;
  loadStatusToHideIntermodal: string[] | null;
  // Appointment Times
  isAppointmentTimeMandatoryIntermodal: boolean;
  showAppointmentTimeInFormAndGridIntermodal: boolean;
  acceptDefaultAppointmentTimesIntermodal: boolean;
  // V1 stores integer minutes for `auto_departure_time_intermodal` (NOT a boolean
  // — diverges from the dispatch-tab `autoDepartureTime`).
  autoDepartureTimeIntermodal: number | null;
  setDefaultAppointmentTimeIntermodal: boolean;
  // V1 stores 'datetime' | 'date' (string) — diverges from dispatch-tab boolean.
  appointmentDayOnlyIntermodal: AppointmentDayOnlyMode | null;
  allowOverlapAppointmentTimeIntermodal: boolean;
  // Categories / Quotes
  isLoadCategoryMandatoryIntermodal: boolean;
  allowCategoriesIntermodal: boolean;
  enableQuotesIntermodal: boolean;
  // Driver Assignment / Trip
  autoAssignDriverIntermodal: boolean;
  enableToBeDeterminedIntermodal: boolean;
  ltlIntermodal: boolean;
  ignoreDriverAssignmentWarningIntermodal: boolean;
  // Settlement Options
  // V1 stores enum string — diverges from the dispatch-tab boolean.
  enableFscPayIntermodal: IntermodalEnableFscPayMode | null;
  enableSettlementOptionForEquipmentIntermodal: boolean;
  enablePaymentOnlyForDriverIntermodal: boolean;
  // Documents / Exports
  enableCojoinedExportsIntermodal: boolean;
  enableDownloadDocumentIntermodal: boolean;
  // Rates / FSC / Routing
  defaultRateTypeIdIntermodal: string | null;
  // V1 column name `intermodal_default_fuel_surcharge_rate_id` (prefix-style anomaly).
  intermodalDefaultFuelSurchargeRateId: string | null;
  deadHeadRoutingRowTypeIntermodal: number | null;

  // ── FTL Brokerage tab (WAYP-807) ───────────────────────────────────
  // Loads / Status (FTL Brokerage subset of dispatch family)
  defaultLoadStatusIdFtlbrokerage: string | null;
  loadStatusToHideFtlbrokerage: string[] | null;
  // Appointment Times
  isAppointmentTimeMandatoryFtlbrokerage: boolean;
  showAppointmentTimeInFormAndGridFtlbrokerage: boolean;
  acceptDefaultAppointmentTimesFtlbrokerage: boolean;
  // V1 stores integer minutes for `auto_departure_time_ftlbrokerage` (mirrors Intermodal).
  autoDepartureTimeFtlbrokerage: number | null;
  setDefaultAppointmentTimeFtlbrokerage: boolean;
  // V1 stores 'datetime' | 'date' (mirrors Intermodal).
  appointmentDayOnlyFtlbrokerage: AppointmentDayOnlyMode | null;
  allowOverlapAppointmentTimeFtlbrokerage: boolean;
  // Categories / Quotes
  isLoadCategoryMandatoryFtlbrokerage: boolean;
  allowCategoriesFtlbrokerage: boolean;
  enableQuotesFtlbrokerage: boolean;
  // Driver Assignment / Trip
  autoAssignDriverFtlbrokerage: boolean;
  enableToBeDeterminedFtlbrokerage: boolean;
  ltlFtlbrokerage: boolean;
  ignoreDriverAssignmentWarningFtlbrokerage: boolean;
  // Settlement Options
  // V1 stores enum string (mirrors Intermodal).
  enableFscPayFtlbrokerage: FtlBrokerageEnableFscPayMode | null;
  enableSettlementOptionForEquipmentFtlbrokerage: boolean;
  enablePaymentOnlyForDriverFtlbrokerage: boolean;
  // Documents / Exports
  enableCojoinedExportsFtlbrokerage: boolean;
  enableDownloadDocumentFtlbrokerage: boolean;
  // Rates / FSC / Routing
  defaultRateTypeIdFtlbrokerage: string | null;
  // V1 column name `ftlbrokerage_default_fuel_surcharge_rate_id` (prefix-style anomaly).
  ftlbrokerageDefaultFuelSurchargeRateId: string | null;
  deadHeadRoutingRowTypeFtlbrokerage: number | null;

  // ── Livestock tab (WAYP-808) ───────────────────────────────────────
  // Loads / Status (Livestock subset of dispatch family)
  defaultLoadStatusIdLivestock: string | null;
  loadStatusToHideLivestock: string[] | null;
  // Appointment Times
  isAppointmentTimeMandatoryLivestock: boolean;
  showAppointmentTimeInFormAndGridLivestock: boolean;
  acceptDefaultAppointmentTimesLivestock: boolean;
  // V1 stores integer minutes (mirrors Intermodal / FTL Brokerage).
  autoDepartureTimeLivestock: number | null;
  setDefaultAppointmentTimeLivestock: boolean;
  // V1 stores 'datetime' | 'date' (mirrors Intermodal / FTL Brokerage).
  appointmentDayOnlyLivestock: AppointmentDayOnlyMode | null;
  allowOverlapAppointmentTimeLivestock: boolean;
  // Categories / Quotes
  isLoadCategoryMandatoryLivestock: boolean;
  allowCategoriesLivestock: boolean;
  enableQuotesLivestock: boolean;
  // Driver Assignment / Trip
  autoAssignDriverLivestock: boolean;
  enableToBeDeterminedLivestock: boolean;
  ltlLivestock: boolean;
  ignoreDriverAssignmentWarningLivestock: boolean;
  // Settlement Options
  // V1 stores enum string (mirrors Intermodal / FTL Brokerage).
  enableFscPayLivestock: LivestockEnableFscPayMode | null;
  enableSettlementOptionForEquipmentLivestock: boolean;
  enablePaymentOnlyForDriverLivestock: boolean;
  // Documents / Exports
  enableCojoinedExportsLivestock: boolean;
  enableDownloadDocumentLivestock: boolean;
  // Rates / FSC / Routing
  defaultRateTypeIdLivestock: string | null;
  // V1 column name `cattlehauling_default_fuel_surcharge_rate_id` — note the
  // "cattlehauling" prefix (NOT "livestock"), even though the tab is Livestock.
  cattlehaulingDefaultFuelSurchargeRateId: string | null;
  deadHeadRoutingRowTypeLivestock: number | null;

  // ── Below-tabs sections (WAYP-808) ─────────────────────────────────
  // Cards rendered below the tabs container, NOT inside any tab.
  // Credit Limit (visible when Credit-Limit enterprise feature is on).
  // V1 stores both as decimal/currency. Prisma surfaces Decimal? as
  // string | null over JSON.
  defaultCreditLimit: string | number | null;
  defaultWarnWithin: string | number | null;
  // Grids (always visible). Page sizes default to 25 (V1 Preferences::$attributes).
  dispatchGridEntries: number | null;
  customerGridEntries: number | null;
  typeGridEntries: number | null;
  transactionGridEntries: number | null;
  refreshTime: number | null;
  // Audit Log Notifications (admin-only, JSON-cast columns from V1
  // Preferences::$casts lines 19-33). Round 6 ships these columns + a
  // basic comma-separated input UX; rich per-event recipient sub-forms ship
  // in a follow-up ticket. Server preserves the raw payload.
  // Note: `delayCharges` (the 11th JSON column) is already declared above —
  // do not duplicate here.
  //
  // WAYP-910: V1 reuses these columns for the USPS Slip Detail card too.
  // The Slip Detail card persists `string[]` of Slip ids (a flat array)
  // while the Audit Log Notifications card persists
  // `Array<[email, statusIds]>`. Server normalizer
  // (settings.service.ts:normalizeRecipientPayload) distinguishes shapes
  // at write-time. Type stays `unknown` so both card UIs can read/write
  // the same field without a discriminator bus.
  autoinvoiceCustomer: unknown;
  routingRowAdded: unknown;
  routingRowTripCancelStop: unknown;
  routingRowDeleted: unknown;
  locationChanged: unknown;
  completeNewLoad: unknown;
  driverArriveLate: unknown;
  uspsLateDeparture: unknown;
  deleteLoad: unknown;
  usps500: unknown;

  // ── WAYP-822: Admin toggle / default columns ─────────────────────────
  // V1 reference: tracxtms/app/EloquentModels/Preferences.php $fillable.
  // Booleans are nullable on the wire — null means "never been set" (treated
  // as false in the form), true/false otherwise.
  payrollEnabled: boolean | null;
  customBrokerEnabled: boolean | null;
  // WAYP-910: type alter Boolean → String for V1 parity. V1 stores these
  // as TEXT minute counts ("15" etc). Slip Detail card surfaces them as
  // minute thresholds. Empty input is sent as `null` from the form.
  showWarningDriverArrives: string | null;
  showWarningDriverTooLateLeaveDestination: string | null;
  showBolByDriver: boolean | null;
  autoWaitingTimeLoadComplete: boolean | null;
  smsLoadRunningLate: boolean | null;
  qbAccount: string | null;
  loadStatusFrom: string | null;
  loadStatusTo: string | null;

  // ── WAYP-823: Disclaimer text ────────────────────────────────────────
  // Multi-paragraph free-form copy shown on customer-facing documents.
  disclaimerText: string | null;

  // ── WAYP-821: Admin-only Organization columns (V1 $fillable port) ────
  // All admin-gated; rendered as sub-cards on the General tab.
  // Notification email lists (CSV, max 255 — V1 contract).
  autoCc: string | null;
  holidayReminderTo: string | null;
  renewalUpdatesTo: string | null;
  peTimechangesReportTo: string | null;
  contractExpiryAlertTo: string | null;
  /**
   * #1487: Structured replacement for `contractExpiryAlertTo` (CSV emails).
   * Array of org user IDs (UUIDs). The CSV column above is still echoed for
   * one release as a rollback escape hatch but the V2 contract-expiry alert
   * job now reads exclusively from this column. Nullable because legacy
   * org rows that have not yet been hydrated by the data-migration script
   * may transit through the API as `null`; the form normalizes to [].
   */
  contractExpiryAlertUserIds: string[] | null;
  dailyReportEmails: string | null;
  dailyReportDaysInAdvance: number | null;
  // Retention.
  purgeHistoryUpTo: number | null;
  // Color preferences (hex strings, validated `^#[0-9A-Fa-f]{6}$`).
  automaticGpsEldColor: string | null;
  manualGpsEldColor: string | null;
  systemUpdateColor: string | null;
  // Pay-period anchors (settlement period boundary calc).
  weekMonth: 'week' | 'month' | null;
  weekDay: number | null; // 0..6 (Sun..Sat)
  monthDay: number | null; // 1..31
  // Suspense accounts (soft FK → chart_of_accounts.id).
  suspenseAccountLoadEarnings: string | null;
  suspenseAccountOtherEarnings: string | null;
  suspenseAccountDeductionAccount: string | null;
  // Recurring-load default frequency ('weekly', 'monthly', etc.).
  defaultFrequency: string | null;
}

export function useCompanyPreferences() {
  return useQuery({
    queryKey: ['settings', 'company-preferences'],
    queryFn: async () => {
      const res = await api.get<{ data: CompanyPreferences }>('/settings/company-preferences');
      return res.data.data;
    },
  });
}

/**
 * V1 parity: the Company Preferences → Grids page-size groups. Each group's
 * value is the default rows-per-page for a family of grids:
 *   - dispatch    → Dispatch + Dispatch History across every load module
 *                   (Freight, USPS, Intermodal, Livestock, FTL Brokerage).
 *   - customer    → Customers, Locations, Drivers, Carriers, Equipment.
 *   - type        → Settings "type"/management grids.
 *   - transaction → Finance grids (Invoices, Custom Charges/Credits,
 *                   Received Payments, Settlements).
 * Mirrors V1's `*_grid_entries` columns (AgGridTrait / dataTableInit).
 */
export type GridPageSizeGroup = 'dispatch' | 'customer' | 'type' | 'transaction';

const GRID_PAGE_SIZE_DEFAULT = 25;

/**
 * Returns the company-default rows-per-page for a grid group, falling back to
 * 25 (the V1 default) while preferences load or when unset. Pages pass the
 * result to `<AgGrid defaultPageSize={...}>`. Because preferences load async,
 * pages should also key the grid on this value (e.g.
 * `key={`grid-${pageSize}`}`) so the grid re-initialises with the resolved
 * size once it arrives — AG Grid locks the page size on first mount.
 */
export function useGridPageSize(group: GridPageSizeGroup): number {
  const { data } = useCompanyPreferences();
  const value =
    group === 'dispatch'
      ? data?.dispatchGridEntries
      : group === 'customer'
        ? data?.customerGridEntries
        : group === 'type'
          ? data?.typeGridEntries
          : data?.transactionGridEntries;
  return value && value > 0 ? value : GRID_PAGE_SIZE_DEFAULT;
}

/**
 * V1 parity: the company `refresh_time` preference (Settings → Company
 * Preferences → Grids) is the auto-refresh interval for the WEEKLY ROLLING
 * WINDOW count strip — and ONLY that. It is NOT a grid-refresh control: in V1
 * `refresh_time ?: 60` is the cache TTL behind the loads-COUNT endpoint
 * (LoadsController::getLoadsCount / Usps/LoadsController), which feeds the day
 * strip / chip counts; the loads grid itself never auto-refreshes.
 *
 * A blank / 0 / unset value falls back to 60s (V1 has no "off" switch). Returns
 * milliseconds, suitable for the rolling-window query's `refetchInterval`.
 *
 * Consumers: the rolling-window count hooks only
 * (`useDispatchLoadsRollingWindow`, `useUspsLoadsRollingWindow`). Do NOT wire
 * this into any loads grid — an earlier change made the whole grid auto-refresh
 * on this setting, which V1 never does; that behavior was reverted and the
 * grid-level auto-refresh mechanism removed entirely.
 */
export const DEFAULT_ROLLING_WINDOW_REFRESH_SECONDS = 60;
export function useRollingWindowRefreshMs(): number {
  const { data } = useCompanyPreferences();
  const seconds = data?.refreshTime;
  return (seconds && seconds > 0 ? seconds : DEFAULT_ROLLING_WINDOW_REFRESH_SECONDS) * 1000;
}

export function useUpdateCompanyPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<CompanyPreferences>) => {
      const res = await api.patch<{ data: CompanyPreferences }>('/settings/company-preferences', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'company-preferences'] });
      // Item 2 (2026-05-29) — the "Date/Time Format on Dispatch Grid"
      // (`dispatchGridDateType`) preference is saved here but consumed by
      // the dispatch / recurring / history grids via the separate
      // `['dispatch-grid-columns', variant]` query (it carries the
      // date-type alongside the column specs). Without this invalidation
      // the grid keeps its cached column defs — and therefore the old
      // DATE/DATETIME formatter — until a hard refresh, so an operator
      // who flips the setting sees no change. Invalidate every variant's
      // column query so the grid rebuilds its Ship/Delivery Date
      // formatters against the new preference immediately.
      queryClient.invalidateQueries({ queryKey: ['dispatch-grid-columns'] });
    },
  });
}

/**
 * V1 parity: Company Preferences → Credit Limit card "Update all existing
 * customers" buttons. POSTs the entered Default Credit Limit / Default
 * Warn-Within to the server, which stamps it on every existing (non-deleted)
 * customer. Mirrors V1 NameAddressController::setDefaultCredit / setDefaultWarn.
 *
 * Invalidates the customers cache so the grid + drawers reflect the new limits
 * immediately (V1 flushed its CUSTOMERS grid cache at the end of the action).
 */
export function useBulkApplyCustomerCreditDefault() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { field: 'creditLimit' | 'warnWithin'; value: number }) => {
      const res = await api.post<{ data: { updated: number } }>(
        '/customers/bulk-credit-default',
        vars,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ---------- Document Naming Hooks (Customizable Document Naming) ----------
// V1 has no equivalent; V2 lets an org rename Tracx-generated documents
// (Company Preferences → Document Naming). Backed by /settings/document-names.

export interface DocumentNameEntry {
  /** Stable internal code (never renamed). */
  code: string;
  /** Tracx default (fallback) name. */
  defaultName: string;
  /** Org-configured custom name, or null when using the default. */
  customName: string | null;
  /** What renders in the app — customName || defaultName. */
  effectiveName: string;
  category: string;
  sortOrder: number;
}

export function useDocumentNames() {
  return useQuery({
    queryKey: ['settings', 'document-names'],
    queryFn: async () => {
      const res = await api.get<{ data: DocumentNameEntry[] }>('/settings/document-names');
      return res.data.data;
    },
  });
}

/**
 * Convenience: a code → effective-name map for labeling menus / buttons.
 * Falls back to the code itself while the query loads.
 */
export function useDocumentNameMap(): Record<string, string> {
  const { data } = useDocumentNames();
  const map: Record<string, string> = {};
  for (const e of data ?? []) map[e.code] = e.effectiveName;
  return map;
}

export function useUpdateDocumentNames() {
  const queryClient = useQueryClient();
  return useMutation({
    // Payload is a map of internal code → custom name ('' or null clears it).
    mutationFn: async (data: Record<string, string | null>) => {
      const res = await api.patch<{ data: DocumentNameEntry[] }>('/settings/document-names', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'document-names'] });
    },
  });
}

// ---------- System Config Hooks ----------

export function useSystemConfig(key: string) {
  return useQuery({
    queryKey: ['settings', 'system', key],
    queryFn: async () => {
      const res = await api.get<{ data: unknown }>(`/settings/system/${key}`);
      return res.data.data;
    },
    enabled: !!key,
  });
}

export function useUpdateSystemConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const res = await api.put<{ data: unknown }>(`/settings/system/${key}`, { value });
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'system', variables.key] });
    },
  });
}

// ---------- Organization Setting Hooks (key-value) ----------

export function useOrgSetting(key: string) {
  return useQuery({
    queryKey: ['settings', 'org', key],
    queryFn: async () => {
      const res = await api.get<{ data: unknown }>(`/settings/org/${key}`);
      return res.data.data;
    },
    enabled: !!key,
  });
}

export function useUpdateOrgSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const res = await api.put<{ data: unknown }>(`/settings/org/${key}`, { value });
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'org', variables.key] });
    },
  });
}

// ---------- Applied Optimizations Hooks ----------

export interface AppliedOptimization {
  id: string;
  organizationId: string;
  fingerprint: string;
  type: string;
  description: string;
  savings: number | null;
  appliedBy: string;
  appliedAt: string;
}

export function useAppliedOptimizations() {
  return useQuery({
    queryKey: ['settings', 'org', 'optimizations', 'applied'],
    queryFn: async () => {
      const res = await api.get<{ data: AppliedOptimization[] }>('/settings/org/optimizations/applied');
      return res.data.data;
    },
  });
}

export function useSaveAppliedOptimization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      fingerprint: string;
      type: string;
      description: string;
      savings?: number;
    }) => {
      const res = await api.post<{ data: AppliedOptimization }>('/settings/org/optimizations/applied', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'org', 'optimizations', 'applied'] });
    },
  });
}

// ---------- Dispatch Preferences Hooks ----------

export interface DispatchPreferences {
  dockPosition: 'left' | 'right' | 'bottom';
  panelWidth: number;
  panelHeight: number;
  viewMode?: string;
}

export function useDispatchPreferences() {
  return useQuery({
    queryKey: ['settings', 'org', 'dispatch-preferences'],
    queryFn: async () => {
      const res = await api.get<{ data: DispatchPreferences }>('/settings/org/preferences');
      return res.data.data;
    },
  });
}

export function useSaveDispatchPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<DispatchPreferences>) => {
      const res = await api.put<{ data: DispatchPreferences }>('/settings/org/preferences', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'org', 'dispatch-preferences'] });
    },
  });
}

// ---------- AI Integration Status Hook ----------

/**
 * Returns whether the Anthropic API key is configured on the server.
 * The key itself is never returned — only a boolean.
 */
export function useAiIntegrationStatus() {
  return useQuery({
    queryKey: ['settings', 'ai', 'status'],
    queryFn: async () => {
      const res = await api.get<{ data: { configured: boolean } }>('/settings/ai/status');
      return res.data.data;
    },
    staleTime: 60_000, // Re-check at most once per minute
  });
}

// ---------- Demo Reset Hook ----------

export function useResetDemoData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{ data: { message: string } }>('/settings/demo/reset');
      return res.data.data;
    },
    onSuccess: () => {
      // Invalidate everything so the UI reloads fresh data
      queryClient.invalidateQueries();
    },
  });
}
